/**
 * Attack animations: the registry, the geometry each one plans from, and the picker.
 *
 * Each animation is a pair of CSS classes (`anim-<id>-attacker` / `anim-<id>-victim`, keyframes in
 * `attacks.css`) plus a `plan` that turns measured card positions into CSS custom properties
 * (distances, angles, delays) and a total duration. Keyframe percentages can't be variables, so
 * anything that depends on the layout goes through those properties.
 *
 * Animations live in *sets*. Every player uses the default set today; `animationSetFor` is the
 * hook for giving each player their own later. A set has separate lists for one victim and for
 * several, since a multi-kill plays differently. The picker never repeats the previous pick.
 */
import type { PlayerId } from '@kadice/shared';
import type { SoundId } from '../audio';

/** A card's on-screen box (viewport pixels) plus the viewport size. */
export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
  cx: number;
  cy: number;
}

export interface Geometry {
  attacker: Box;
  victims: Box[];
  vw: number;
  vh: number;
  /**
   * Portrait Layout, when the attacker leaves the big seat after this attack: its seat's Kill
   * Quest window in the column (where it lands, if the animation takes it offscreen). Null: it
   * goes back where it started.
   */
  home: Box | null;
}

/** CSS custom properties for one element (values include units). */
export type Vars = Record<string, string>;

/**
 * A sprite effect (or sound) to spawn during the animation, in viewport pixels, `at` ms from the start.
 * Hits, dash and cloud play PNG frames once; the blast is a looping GIF that flies from
 * (x, y) to (tx, ty) in `dur` ms and is gone on arrival.
 */
export type Effect =
  | { kind: 'sound'; at: number; sound: SoundId }
  | { kind: 'hit'; at: number; x: number; y: number; size: number; sets: number[] }
  | { kind: 'cloud'; at: number; x: number; y: number; size: number }
  | { kind: 'dash'; at: number; x: number; y: number; size: number; angle: number }
  | {
      kind: 'blast';
      at: number;
      x: number;
      y: number;
      tx: number;
      ty: number;
      size: number;
      angle: number;
      dur: number;
    };

export interface AttackPlan {
  attacker: Vars;
  victims: Vars[];
  /** Each victim's travel (px, y down) while it is flying off: its dice fly the same general way. */
  flights: { x: number; y: number }[];
  /** Milliseconds from the start until the attacker lands, when the table shakes. null: no shake. */
  shakeAt: number | null;
  /** Total milliseconds until every element has finished. */
  duration: number;
  effects: Effect[];
  /** The attacker ended in its seat in the column (`Geometry.home`), not where it started. */
  home: boolean;
}

export interface AttackAnimation {
  id: string;
  plan(geo: Geometry): AttackPlan;
}

export interface AnimationSet {
  /** One card destroyed. */
  single: AttackAnimation[];
  /** Two or more cards destroyed at once. */
  multi: AttackAnimation[];
}

/** Pause between the strike and the victim reacting (none: victims launch on impact). */
const REACT_MS = 0;
/** How far past the screen edge a card must be to be fully out of sight. */
const MARGIN = 40;
/** Squash wind-up before a jump: the jump itself starts this many ms into the animation. */
const SQ = 250;

const px = (n: number) => `${Math.round(n)}px`;
const ms = (n: number) => `${Math.round(n)}ms`;
const deg = (n: number) => `${Math.round(n)}deg`;
const sign = (n: number) => (Math.abs(n) < 8 ? (Math.random() < 0.5 ? -1 : 1) : Math.sign(n));

/** Unit vector from the attacker to a victim, and the distance. */
function aim(geo: Geometry, v: Box) {
  const dx = v.cx - geo.attacker.cx;
  const dy = v.cy - geo.attacker.cy;
  const dist = Math.hypot(dx, dy) || 1;
  return { dx, dy, dist, ux: dx / dist, uy: dy / dist };
}

/** Impact flash on a card. Sets 2 and 3 are the standard hits; 4 and 5 go with the energy blast. */
const hitFx = (at: number, b: Box, sets = [2, 3]): Effect => ({
  kind: 'hit',
  at,
  x: b.cx,
  y: b.cy,
  size: Math.max(96, b.width * 0.9),
  sets,
});
/** Smoke kicked up where a card lands, resting on the bottom edge of `b`, at least a card wide. */
const cloudFx = (at: number, b: Box): Effect => {
  const size = b.width * 1.8;
  return { kind: 'cloud', at, x: b.cx, y: b.top + b.height - size * 0.3, size };
};
/** Smoke dash on the attacker's centre at the start of a jump, pointing at (dx, dy). */
const dashFx = (at: number, a: Box, dx: number, dy: number): Effect => ({
  kind: 'dash',
  at,
  x: a.cx,
  y: a.cy,
  size: a.width * 1.3,
  angle: (Math.atan2(dy, dx) * 180) / Math.PI,
});

/**
 * A seat in Portrait Layout's column shows only the card's power window (`.side-list .strip-window`):
 * the power circle with its frame and name, x 26-344 and y 342-640 of the 370 x 640 art, as shares.
 */
const WINDOW = { left: 26 / 370, right: 344 / 370, top: 342 / 640 };

/**
 * Coming back from offscreen at the size of its seat in the column (`geo.home`) and landing there:
 * `--k` the scale, `--hx`/`--hy` the move that puts the scaled card's power window over the seat's
 * (`origin`: what the card scales around), `--clip` the crop down to that window (an `inset()`,
 * before the scale; it starts from the whole card).
 * With no home: no scale, no move, no crop (back where it started).
 */
function homeVars(geo: Geometry, origin: 'bottom' | 'center'): { k: number; vars: Vars } {
  const a = geo.attacker;
  const h = geo.home;
  if (!h) return { k: 1, vars: { '--k': '1', '--hx': '0px', '--hy': '0px', '--clip': 'inset(0 0 0 0 round 0px)' } };
  const k = h.width / ((WINDOW.right - WINDOW.left) * a.width);
  const tall = a.height * k;
  const cy = (WINDOW.top + 1) / 2;
  const hy = origin === 'bottom' ? h.cy - (a.top + a.height) + tall * (1 - cy) : h.cy - a.cy - tall * (cy - 0.5);
  const pct = (n: number) => `${(n * 100).toFixed(2)}%`;
  return {
    k,
    vars: {
      '--k': k.toFixed(4),
      '--hx': px(h.cx - a.cx),
      '--hy': px(hy),
      '--clip': `inset(${pct(WINDOW.top)} ${pct(1 - WINDOW.right)} 0 ${pct(WINDOW.left)} round ${px(6 / k)})`,
    },
  };
}

/** Distance to move a card up so it is fully above the viewport. */
const offTop = (b: Box) => b.top + b.height + MARGIN;
/** Distance to move a card down so it is fully below the viewport. */
const offBottom = (b: Box, vh: number) => vh - b.top + MARGIN;

// ---------------------------------------------------------------------------
// Single victim
// ---------------------------------------------------------------------------

/** Lean back from the enemy (pivoting on the bottom centre), snap forward and dash through. Victim spins up and away. */
const lunge: AttackAnimation = {
  id: 'lunge',
  plan(geo) {
    const v = geo.victims[0]!;
    const { dx, dy, ux } = aim(geo, v);
    const s = sign(dx);
    const total = 1500;
    const hit = total * 0.46;
    const flight = { x: ux * geo.vh * 0.7 + s * 120, y: -offTop(v) };
    return {
      attacker: {
        '--lean-back': deg(-s * 28),
        '--lean-fwd': deg(s * 14),
        '--through-x': px(dx * 1.25),
        '--through-y': px(dy * 1.25),
        '--dur': ms(total),
      },
      victims: [
        {
          '--fly-x': px(flight.x),
          '--fly-y': px(flight.y),
          '--spin': deg(s * 1080),
          '--delay': ms(hit + REACT_MS),
          '--dur': ms(650),
        },
      ],
      flights: [flight],
      shakeAt: null,
      duration: hit + REACT_MS + 650,
      effects: [hitFx(hit, v), { kind: 'sound', at: hit, sound: 'collide' }],
      home: false,
    };
  },
};

/**
 * Leap off the top of the screen, pause, then crash down on the victim; it is sent spinning down
 * and off. Then back where it started; or, leaving the big seat (`geo.home`), it comes down at its
 * seat's size and goes on into its seat.
 */
const stomp: AttackAnimation = {
  id: 'stomp',
  plan(geo) {
    const v = geo.victims[0]!;
    const { dx, dy } = aim(geo, v);
    const s = sign(dx);
    const main = 1700;
    const hit = SQ + main * 0.8;
    const { k, vars } = homeVars(geo, 'bottom');
    const ret = geo.home ? 550 : 450;
    const flight = { x: s * 160, y: offBottom(v, geo.vh) };
    return {
      attacker: {
        '--dx': px(dx),
        // Scaled from its bottom edge: the centre lands on the victim's.
        '--dy': px(dy - ((1 - k) * geo.attacker.height) / 2),
        '--up': px(offTop(geo.attacker)),
        '--dur': ms(main),
        '--sq': ms(SQ),
        '--ret': ms(ret),
        '--ret-delay': ms(SQ + main + 50),
        ...vars,
      },
      victims: [
        {
          '--fly-x': px(flight.x),
          '--fall': px(flight.y),
          '--spin': deg(s * 900),
          '--delay': ms(hit + REACT_MS),
          '--dur': ms(600),
        },
      ],
      flights: [flight],
      shakeAt: hit,
      duration: Math.max(SQ + main + 50 + ret, hit + REACT_MS + 600),
      effects: [
        dashFx(SQ, geo.attacker, dx, dy),
        hitFx(hit, v, [3]),
        cloudFx(hit, v),
        { kind: 'sound', at: hit, sound: 'stomp' },
      ],
      home: !!geo.home,
    };
  },
};

/**
 * Leap up toward the victim and off the top of the screen; the screen wraps, so the card
 * reappears from the bottom and keeps flying the same straight line into the victim,
 * who twirls up a little and drops away. Leaving the big seat (`geo.home`), it reappears at its
 * seat's size, and after the strike rises from the bottom into its seat instead of where it started.
 */
const wrap: AttackAnimation = {
  id: 'wrap',
  plan(geo) {
    const a = geo.attacker;
    const v = geo.victims[0]!;
    const dx = v.cx - a.cx;
    const dy = v.cy - a.cy;
    // The screen is a torus this tall (viewport plus a card and margins each side), so the
    // wrap jump happens while the card is out of sight at both ends.
    const H = geo.vh + a.height + 2 * MARGIN;
    // The line to the victim's copy one screen up: this is the straight path across the wrap.
    const tx = dx;
    const ty = dy - H;
    const exitY = -(a.cy + a.height / 2 + MARGIN); // card fully off the top
    const s1 = Math.min(0.95, Math.max(0.05, exitY / ty));
    const flight = 325;
    const t0 = SQ; // flight starts after the squash
    const d1 = flight * s1;
    const d2 = flight - d1;
    const rot = (Math.atan2(tx, -ty) * 180) / Math.PI;
    const s = sign(dx);
    // Scaled around its centre, so the strike needs no correction; only the way home does.
    const { vars } = homeVars(geo, 'center');
    const hx = parseFloat(vars['--hx']!);
    const hy = parseFloat(vars['--hy']!);
    // After the strike it keeps going: through the ceiling again (drifting back into line with
    // its seat on the way), in from the bottom, and eases up into place. Same speed as the flight.
    const speed = Math.hypot(tx, ty) / flight;
    const d3 = Math.max(150, Math.hypot(dx - hx, dy - exitY) / speed);
    const d4 = Math.max(250, (exitY + H - hy) / speed);
    const d3Delay = t0 + flight + 120;
    const d4Delay = d3Delay + d3;
    const end = d4Delay + d4;
    const fall = { x: s * 90, y: offBottom(v, geo.vh) };
    return {
      attacker: {
        '--ex': px(s1 * tx),
        '--ey': px(exitY),
        '--ey2': px(exitY + H),
        '--dx': px(dx),
        '--dy': px(dy),
        '--rot': deg(rot),
        '--sq': ms(SQ),
        '--d1': ms(d1),
        '--d2': ms(d2),
        '--d3': ms(d3),
        '--d3-delay': ms(d3Delay),
        '--d4': ms(d4),
        '--d4-delay': ms(d4Delay),
        ...vars,
      },
      victims: [
        {
          '--fly-x': px(fall.x),
          '--fall': px(fall.y),
          '--spin': deg(s * 720),
          '--delay': ms(t0 + flight + REACT_MS),
          '--dur': ms(650),
        },
      ],
      flights: [fall],
      shakeAt: null,
      duration: Math.max(end, t0 + flight + REACT_MS + 650),
      effects: [dashFx(t0, a, dx, dy), hitFx(t0 + flight, v), { kind: 'sound', at: t0 + flight, sound: 'collide' }],
      home: !!geo.home,
    };
  },
};

/**
 * Lean back, snap forward and fire an energy blast from the card's centre at the victim, who
 * is hit by it (hit sets 4-5) and spun up and away.
 */
const blast: AttackAnimation = {
  id: 'blast',
  plan(geo) {
    const a = geo.attacker;
    const v = geo.victims[0]!;
    const { dx, dist } = aim(geo, v);
    const s = sign(dx);
    const total = 1400;
    const fireAt = total * 0.44;
    const lean = s * 14;
    // The lean pivots on the bottom centre, so the card's centre swings a little.
    const t = (lean * Math.PI) / 180;
    const sx = a.cx + (a.height / 2) * Math.sin(t);
    const sy = a.top + a.height - (a.height / 2) * Math.cos(t);
    const bx = v.cx - sx;
    const by = v.cy - sy;
    const travel = Math.max(120, Math.hypot(bx, by) / 2.5);
    const hit = fireAt + travel;
    const flight = { x: (dx / dist) * geo.vh * 0.7 + s * 120, y: -offTop(v) };
    return {
      attacker: { '--lean-back': deg(-s * 20), '--lean-fwd': deg(lean), '--dur': ms(total) },
      victims: [
        {
          '--fly-x': px(flight.x),
          '--fly-y': px(flight.y),
          '--spin': deg(s * 1080),
          '--delay': ms(hit),
          '--dur': ms(650),
        },
      ],
      flights: [flight],
      shakeAt: null,
      duration: Math.max(total, hit + 650),
      effects: [
        {
          kind: 'blast',
          at: fireAt,
          x: sx,
          y: sy,
          tx: v.cx,
          ty: v.cy,
          size: Math.max(90, a.width),
          angle: (Math.atan2(by, bx) * 180) / Math.PI,
          dur: travel,
        },
        hitFx(hit, v, [4, 5]),
        { kind: 'sound', at: hit, sound: 'energyBlastImpact' },
      ],
      home: false,
    };
  },
};

// ---------------------------------------------------------------------------
// Several victims
// ---------------------------------------------------------------------------

/** Hop high, hang for half a second, then stomp the ground everyone stands on; every victim is thrown down and away, nearest first. */
const quake: AttackAnimation = {
  id: 'quake',
  plan(geo) {
    const total = 2000;
    const hit = SQ + total * 0.56;
    const reach = Math.max(...geo.victims.map((v) => aim(geo, v).dist), 1);
    let end = SQ + total;
    const effects: Effect[] = [];
    const flights: { x: number; y: number }[] = [];
    const victims = geo.victims.map((v) => {
      const { dx, dist } = aim(geo, v);
      const s = sign(dx);
      const delay = hit + REACT_MS + (dist / reach) * 80;
      end = Math.max(end, delay + 600);
      effects.push(hitFx(delay, v, [3]));
      const fall = { x: s * (100 + Math.random() * 140), y: offBottom(v, geo.vh) };
      flights.push(fall);
      return {
        '--fly-x': px(fall.x),
        '--fall': px(fall.y),
        '--spin': deg(s * (720 + Math.random() * 360)),
        '--delay': ms(delay),
        '--dur': ms(600),
      };
    });
    // The jump heads toward the middle of the group; the ground-pound raises smoke under the attacker.
    const mx = geo.victims.reduce((n, v) => n + v.cx, 0) / geo.victims.length - geo.attacker.cx;
    const my = geo.victims.reduce((n, v) => n + v.cy, 0) / geo.victims.length - geo.attacker.cy;
    effects.push(dashFx(SQ, geo.attacker, mx, my), cloudFx(hit, geo.attacker), {
      kind: 'sound',
      at: hit,
      sound: 'stomp',
    });
    return {
      attacker: { '--dur': ms(total), '--sq': ms(SQ) },
      victims,
      flights,
      shakeAt: hit,
      duration: end,
      effects,
      home: false,
    };
  },
};

// ---------------------------------------------------------------------------
// Sets and picking
// ---------------------------------------------------------------------------

export const ANIMATION_SETS: Record<string, AnimationSet> = {
  default: { single: [lunge, stomp, wrap, blast], multi: [quake] },
};

/** Which animation set a player uses. Everyone shares the default for now. */
export function animationSetFor(_playerId: PlayerId): string {
  return 'default';
}

const lastPick = new Map<string, string>();

/** A random animation from the attacker's set for this many victims, never the one used last time. */
export function pickAttackAnimation(attackerId: PlayerId, victimCount: number): AttackAnimation {
  const setId = animationSetFor(attackerId);
  const kind = victimCount > 1 ? 'multi' : 'single';
  const list = (ANIMATION_SETS[setId] ?? ANIMATION_SETS['default']!)[kind];
  const key = `${setId}:${kind}`;
  const options = list.length > 1 ? list.filter((a) => a.id !== lastPick.get(key)) : list;
  const pick = options[Math.floor(Math.random() * options.length)]!;
  lastPick.set(key, pick.id);
  return pick;
}
